import * as React from 'react';
import {Box} from '@mui/material';

// Fallback until the bar's real height is measured.
const DEFAULT_BOTTOM_BAR_CLEARANCE = 72;

interface MobileStickyActionBarProps {
    readonly children: React.ReactNode;
}

/**
 * Fixed bottom action bar for mobile pages (same look as the Payroll Run "Calculate" bar).
 * Also renders an in-flow spacer of the bar's measured height so page content is never covered.
 */
export default function MobileStickyActionBar({children}: Readonly<MobileStickyActionBarProps>): React.ReactElement {
    const barRef = React.useRef<HTMLDivElement | null>(null);
    const [height, setHeight] = React.useState(DEFAULT_BOTTOM_BAR_CLEARANCE);

    React.useLayoutEffect(() => {
        const el = barRef.current;
        if (!el) return;
        const update = () => setHeight(el.offsetHeight);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <Box sx={{height: `${height + 16}px`}} aria-hidden/>
            <Box
                ref={barRef}
                sx={{
                    position: 'fixed', left: 0, right: 0, bottom: 0, p: 1.5,
                    backgroundColor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
                    zIndex: (t) => t.zIndex.appBar,
                    display: 'flex', gap: 1, alignItems: 'center',
                }}
            >
                {children}
            </Box>
        </>
    );
}
